import { PackageResult, PricingOrderResult, PromoCodeBySubject } from './types';

/**
 * The renewal payment forms' price summary, derived from a pricing order rather than
 * from the retired renewal-subscriptions response. Field names match what those forms
 * already render, so the payment screen is unchanged.
 */
export type RenewalPriceInfo = {
  price: number;
  vat: number;
  discount: number;
  total: number;
  grossUndiscountedPrice: number;
  grossDiscountedPrice: number;
  discountedPrice: number;
  vatRate: number;
  /** subjectsID -> packagesID, the payment payload's package list. */
  packageId: { [subjectsId: number]: number };
  subjectId: number[];
  /** True when the priced basket actually took a promo discount. */
  promoCodeApplied: boolean;
  /** Every distinct code that discounted a package. */
  appliedPromoCodes: string[];
  /** The code pricing carried for each subject, won or not. */
  promoCodeBySubject: PromoCodeBySubject;
};

export type RenewalSelectedPackage = {
  subjectId: number;
  subjectName: string;
  packageId: number;
  subscriptionLength: string;
  packagePrice: number;
  ageGroup: string;
  discountedPrice: number;
};

const packagesOf = (order: PricingOrderResult) =>
  (order && order.orderDiscountResult && order.orderDiscountResult.packageResults) || [];

/** Every figure comes from the pricing order; nothing is recomputed on the client. */
export const buildRenewalPriceInfo = (order: PricingOrderResult): RenewalPriceInfo => {
  const totals = (order && order.orderDiscountResult) || ({} as any);
  const packages = packagesOf(order);

  const discounted = packages.filter((p) => !!p.promoCodeDiscountApplied);

  const packageId: { [subjectsId: number]: number } = {};
  const subjectId: number[] = [];
  const promoCodeBySubject: PromoCodeBySubject = {};

  packages.forEach((p) => {
    packageId[p.subjectsID] = p.packagesID;
    if (subjectId.indexOf(p.subjectsID) === -1) {
      subjectId.push(p.subjectsID);
    }
    if (p.promoCode) {
      promoCodeBySubject[p.subjectsID] = p.promoCode;
    }
  });

  // Several codes can discount one basket, so collect the winners rather than sampling one.
  const appliedPromoCodes = discounted.reduce(
    (codes: string[], p) => (p.promoCode && codes.indexOf(p.promoCode) === -1 ? [...codes, p.promoCode] : codes),
    []
  );

  return {
    price: totals.totalNetUndiscounted || 0,
    vat: totals.totalVatOnDiscounted || 0,
    discount: totals.totalNetSaving || 0,
    total: totals.totalGrossDiscounted || 0,
    grossUndiscountedPrice: totals.totalGrossUndiscounted || 0,
    grossDiscountedPrice: totals.totalGrossDiscounted || 0,
    discountedPrice: totals.totalNetDiscounted || 0,
    vatRate: totals.vatRate || 0,
    packageId,
    subjectId,
    promoCodeApplied: discounted.length > 0,
    appliedPromoCodes,
    promoCodeBySubject,
  };
};

/** One summary row per package in the basket, in the order the pricing response returned them. */
export const buildRenewalSelectedPackages = (order: PricingOrderResult): RenewalSelectedPackage[] =>
  packagesOf(order).map((p) => ({
    subjectId: p.subjectsID,
    subjectName: p.subjectsName,
    packageId: p.packagesID,
    subscriptionLength: p.subscriptionTypesName,
    packagePrice: p.netUndiscounted,
    ageGroup: p.generalPackagesName,
    discountedPrice: p.netDiscounted,
  }));

/** subjectsID -> what a renewal panel last priced that subject's package at. */
export type RenewalPricesBySubject = {
  [subjectsId: number]: { netUndiscounted: number; netDiscounted: number };
};

export const renewalPricesBySubject = (packages: PackageResult[]): RenewalPricesBySubject => {
  const prices: RenewalPricesBySubject = {};
  packages.forEach((p) => {
    prices[p.subjectsID] = { netUndiscounted: p.netUndiscounted, netDiscounted: p.netDiscounted };
  });
  return prices;
};

const isPrice = (value: number | null | undefined): value is number =>
  typeof value === 'number' && isFinite(value);

/**
 * The subscription card's renewal price. The undiscounted price is struck through only when the
 * discounted one is lower: pricing already decides which discounts apply.
 */
export const renewalCardPrice = (
  undiscounted: number | null | undefined,
  discounted: number | null | undefined
): { price: number; was: number | null } | null => {
  if (isPrice(discounted)) {
    return { price: discounted, was: isPrice(undiscounted) && undiscounted > discounted ? undiscounted : null };
  }

  return isPrice(undiscounted) ? { price: undiscounted, was: null } : null;
};
